import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { EmployeesDialog } from "src/components/Dialogs/EmployeeDialog/EmployeeDialog";
import { RightEmployeeDrawer } from "src/components/RightDrawer/RightEmployeeDrawer";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
  ChevronDown,
  ChevronUp,
  SearchIcon,
} from "src/Icons";
import { getCategoryTemplates } from "src/store/reducers/categoryTemplatesSlice";
import {
  showEmployeesDialog,
} from "src/store/reducers/dialogSlice";
import { openDrawer } from "src/store/reducers/drawerSlice";
import {
  getEmployees,
  getFilteredEmployees,
  updateSearchParams,
} from "src/store/reducers/employeesSlice";
import { getServiceTemplates } from "src/store/reducers/serviceTemplatesSlice";
import { getStudios } from "src/store/reducers/studiosSlice";
import { setSelectedRow } from "src/store/reducers/tableSlice";
import { getSearchParams } from "src/utils/search";

import { EmptyPageState, MainPage, Table, ToggleBox } from "../../components";
import { SortByMenuStyleProps } from "../CommonComponentsStyles";

import { columns, paramsCatalogue } from "./EmployeesScreen.data";
import { SortTypes } from "./EmployeesScreen.types";

export function EmployeesScreen () {
  const dispatch = useAppDispatch();

  const {
    employees,
    filteredEmployees,
    searchParams: isSearching,
  } = useAppSelector((state) => state.employees);
  const { isEmployeesDialogOpen } = useAppSelector((state) => state.dialog);
  const {
    isEmployeesLoading,
    isFilteredEmployeesLoading,
    selectedToggleBoxValue: toogleBoxValue,
    searchInputValue: searchValue,
    sortByValue: sortValue,
  } = useAppSelector((state) => state.employees);

  const [selectedToggleBox, setSelectedToggleBox] =
    useState<string>(toogleBoxValue);
  const [sortBy, setSortBy] = useState<string>(sortValue);
  const [searchInput, setSearchInput] = useState(searchValue);
  const [isSortSelectOpen, setIsSortSelectOpen] = useState<boolean>(false);

  const handleChange = (nextSelected: string) => {
    setSelectedToggleBox(nextSelected);
  };

  const handleChangeSortBy = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as SortTypes);
    dispatch(updateSearchParams({ selectedToggleBox, searchInput, sortBy }));
    dispatch(
      getFilteredEmployees(
        getSearchParams(
          paramsCatalogue,
          event.target.value,
          selectedToggleBox,
          searchInput
        )
      )
    );
  };

  const handleSearchClick = () => {
    dispatch(updateSearchParams({ selectedToggleBox, searchInput, sortBy }));
    dispatch(
      getFilteredEmployees(
        getSearchParams(paramsCatalogue, sortBy, selectedToggleBox, searchInput)
      )
    );
  };

  const handleOpenEmployeesDialog = () => {
    dispatch(showEmployeesDialog());
  };

  const onRowClick = (params: any) => {
    dispatch(setSelectedRow(params.row));
    dispatch(openDrawer());
  };

  useEffect(() => {
    dispatch(getStudios());
    dispatch(getEmployees());
    dispatch(getCategoryTemplates());
    dispatch(getServiceTemplates());
  }, []);

  return (
    <MainPage
      DialogComp={<EmployeesDialog isOpen={isEmployeesDialogOpen} />}
      DrawerComp={<RightEmployeeDrawer />}
      EmptyStateComp={
        <EmptyPageState
          buttonText={"Add employee"}
          image="/images/empty-page-states/employees.png"
          subTitle="To start managing your staff members, you can add new employees, assign them to specific studios or roles, set their availability and schedules, and define their expertise areas."
          title="Currently, there are no employees set up in the system."
          onButtonClick={handleOpenEmployeesDialog}
        />
      }
      addButtonText={"Add employee"}
      isDialogOpen={isEmployeesDialogOpen}
      isShouldShowEmptyState={!employees.length && !isEmployeesLoading}
      title="Employees"
      onAddButtonClick={handleOpenEmployeesDialog}
    >
      <main className="main-page">
        <Box
          display="flex"
          style={{
            padding: "24px 16px 0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flex: 1,
            }}
          >
            <Box
              sx={{
                border: "1px solid #D6D6DB",
                borderBottomLeftRadius: "8px",
                borderTopLeftRadius: "8px",
                borderRight: "none",
                display: "flex",
                alignItems: "center",
                paddingX: 2,
                paddingY: 1,
                color: "#1F1F1F",
                fontWeight: "500",
              }}
            >
              <ToggleBox
                label="Studio"
                selected={selectedToggleBox}
                onSelected={handleChange}
              />
              <ToggleBox
                label="Service"
                selected={selectedToggleBox}
                onSelected={handleChange}
              />
              <ToggleBox
                label="Rank"
                selected={selectedToggleBox}
                onSelected={handleChange}
              />
            </Box>
            <Box
              display="flex"
              sx={{
                border: "1px solid #D6D6DB",
                backgroundColor: "#FAFAFA",
                borderTopRightRadius: "8px",
                borderBottomRightRadius: "8px",
                flex: 1,
                maxWidth: "296px",
              }}
            >
              <InputBase
                placeholder="Search"
                sx={{ ml: 2, flex: 1, fontSize: "14px" }}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <IconButton type="button" onClick={handleSearchClick}>
                <SearchIcon />
              </IconButton>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
            }}
          >
            <Box sx={{ minWidth: 120, marginLeft: "24px" }}>
              <FormControl fullWidth>
                <InputLabel id="select-sort-by-label">Sort by</InputLabel>
                <Select
                  IconComponent={() => (
                    <>
                      {isSortSelectOpen ? (
                        <ChevronUp fontSize="inherit" />
                      ) : (
                        <ChevronDown fontSize="inherit" />
                      )}
                    </>
                  )}
                  MenuProps={SortByMenuStyleProps}
                  id="select-sort-by"
                  label="Sort by"
                  labelId="select-sort-by-label"
                  sx={{
                    borderRadius: "8px",
                    color: "#1F1F1F",
                    fontWeight: "500",
                    height: "48px",

                    "& .MuiSvgIcon-root": {
                      marginRight: "14px",
                      color: isSortSelectOpen ? "#BF295C" : "#676767",
                    },
                  }}
                  value={sortBy}
                  onChange={handleChangeSortBy}
                  onClose={() => setIsSortSelectOpen(false)}
                  onOpen={() => setIsSortSelectOpen(true)}
                >
                  <MenuItem disableRipple value={SortTypes.NAME}>
                    Name
                  </MenuItem>
                  <MenuItem disableRipple value={SortTypes.SERVICES}>
                    Services
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        <Box
          style={{
            padding: "32px 16px",
            paddingBottom: employees.length === 0 ? "72px" : undefined,
          }}
        >
          <Table
            columns={columns}
            loading={isEmployeesLoading || isFilteredEmployeesLoading}
            rows={isSearching ? filteredEmployees : employees}
            onRowClick={onRowClick}
          />
        </Box>
      </main>
    </MainPage>
  );
}

